import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationService,
    StudentPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    Organization,
    StudentPersonalInfoResponse,
    Metadata,
    hasFailedInstantMatch,
    CollegeNameComponent as CollegeName,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
    FormFooterComponent as FormFooter,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    submitForm,
    setRef,
    VerificationStepsEnum,
    MarketConsentWrapper,
    getSafe,
    FieldId,
    FieldIdEnum,
    FormValidationOptions,
    getCustomValidator,
    getCustomValidatorFields,
    validateFieldById,
    ViewModel,
} from '@sheerid/jslib';

import { PostalCodeComponent } from './PostalCodeComponent'
import { LogoHeaderComponent } from './LogoHeaderComponent'


interface StepStudentPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({ intl, verificationService }: StepStudentPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as StudentPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: StudentPersonalInfoViewModel = produce(viewModel, (draft: StudentPersonalInfoViewModel) => {
            // @ts-ignore
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const isFormFilled = (
        viewModel: ViewModel,
        formValidationOptions?: FormValidationOptions,
      ): boolean => {
        const customValidatorFields = getCustomValidatorFields();

        // TODO fix next line
        // eslint-disable-next-line
        for (const fieldId in viewModel) {
          const fieldValue = viewModel[fieldId] ? viewModel[fieldId] : "";
          const isFieldSkipped =
            viewModel.fieldsToSkipValidation &&
            viewModel.fieldsToSkipValidation.includes(fieldId as FieldId);

          if (
            FieldIdEnum[fieldId] &&
            typeof validateFieldById(fieldId, fieldValue, formValidationOptions) === "string" &&
            !isFieldSkipped
          ) {
            return false;
          }
        }

        const metadata = getSafe(() => (viewModel as any).metadata, null);
        if (metadata !== null) {
          // TODO fix next line
          // eslint-disable-next-line
          for (const fieldId in metadata) {
            const fieldValue = metadata[fieldId] !== undefined ? metadata[fieldId] : "";

            if (
              customValidatorFields.includes(fieldId) &&
              typeof getCustomValidator(fieldId)(fieldValue, formValidationOptions) === "string"
            ) {
              return false;
            }
          }
        }

        return true;
    };


    return (
        <div id="sid-step-student-personal-info" className="sid-l-container">
            <LogoHeaderComponent />
            { failedInstantMatch ?
                <div className="sid-header">

                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.title"
                            defaultMessage="Check Your Name &amp; Birthdate" />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and birthdate must match what is on your official school records." />
                    </div>
                </div>
            :
                <div className="sid-header">

                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Unlock this Student-Only Offer" />
                    </div>

                    <div className="sid-header__subtitle">
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a student at a degree-granting college or university." />
                        &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            <div className="sid-header__title sid-l-space-top-lg">
                <h1>
                    <FormattedHTMLMessage
                        id="step.personalInfo.verifyYourDetails"
                        defaultMessage="Verify your details" />
                </h1>
            </div>

            <div className="sid-step-personalInfo-req-fields">
                <span>
                    <FormattedHTMLMessage
                        id="step.personalInfo.reqField"
                        defaultMessage="All fields are required." />
                </span>
            </div>

            <CollegeName
                value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                placeholder={
                    intl.formatMessage({
                        id: "universityPlaceholder",
                        defaultMessage: "University",
                        })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder={
                        intl.formatMessage({
                            id: "firstNamePlaceholder",
                            defaultMessage: "First Name",
                            })
                    }
                    onChange={
                        (newValue) => {
                            updateStudentViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder={
                        intl.formatMessage({
                            id: "lastNamePlaceholder",
                            defaultMessage: "Last Name",
                            })
                    }
                    onChange={
                        (newValue) => {
                            updateStudentViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateStudentViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={
                    intl.formatMessage({
                        id: "emailAddressPlaceholder",
                        defaultMessage: "Email Address",
                        })
                }
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal or school email is fine"
                    />
                }
                onChange={
                    (newValue) => {
                        updateStudentViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />

            <PostalCodeComponent
                isErrored={!!fieldValidationErrors.postalCode}
                placeholder={
                    intl.formatMessage({
                        id: "postalCodePlaceholder",
                        defaultMessage: "Home Zip Code",
                        })
                }
                onChange={(newValue: any) => {
                    updateStudentViewModel(
                        'metadata',
                        {
                            postalCode: newValue,
                        },
                    );
                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                }}
                value={viewModel.hasOwnProperty('metadata') ? viewModel.metadata.postalCode : undefined}
            />

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                updateStudentViewModel("metadata", {
                    ...verificationService.viewModel.metadata,
                    marketConsentValue: newValue,
                });
                updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-wrapper__collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectStudentPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={button => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage
                                id="tryAgain"
                                defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage
                                id="verifyMyStudentStatus"
                                defaultMessage="Verify My Student Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="studentInfoShared"
                    defaultMessage="Information entered here will be used for verification purposes."
                />
                {
                    viewModel.countryChoice && viewModel.countryChoice.value !== 'US' ?
                        <FormattedHTMLMessage
                            id="informationTransferredToUS"
                            defaultMessage="Your information will be transferred to the United States and will be treated according to SheerID’s privacy policy."
                        />
                    : null
                }
            </FormFooter>
        </div>
    );
};

export const StepStudentPersonalInfoComponent = injectIntl(StepStudentPersonalInfo);
